import { useEffect, useRef, useState } from "react";
import {
    FaChevronDown,
    FaMagnifyingGlass,
    FaUser,
    FaSpinner, // Ditambahkan untuk loading
} from "react-icons/fa6";
import { useHookPppoeUser } from "../../../hooks/useHookPppoeUser";

export const SelectPppoeUser = ({
    label,
    value,
    setValue,
    onChange,
    placeholder = "Pilih Pelanggan",
    searchable = true,
}) => {
    const {
        data: rawData,
        loading: isLoadingData,
        setParams,
    } = useHookPppoeUser();

    const [open, setOpen] = useState(false);
    const [localSearch, setLocalSearch] = useState(""); // Hanya untuk mengontrol inputan UI
    const debounceRef = useRef(null);
    const wrapperRef = useRef(null);

    // Set limit awal lebih besar agar cocok untuk dropdown (opsional, sesuaikan kebutuhan)
    useEffect(() => {
        setParams(prev => ({ ...prev, limit: 50 }));
    }, [setParams]);

    // Support API {data:[...]} atau langsung [...]
    const safeData = Array.isArray(rawData?.data)
        ? rawData.data
        : Array.isArray(rawData)
            ? rawData
            : [];

    // Close dropdown jika klik di luar & reset pencarian
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
                setLocalSearch("");
                // Reset param search di server agar data kembali lengkap saat dibuka lagi
                setParams(prev => ({ ...prev, search: "" })); 
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [setParams]);

    // ==========================================
    // DEBOUNCE: Mengirim pencarian ke Server
    // ==========================================
    useEffect(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        // Jeda 400ms setelah user berhenti mengetik
        debounceRef.current = setTimeout(() => {
            setParams(prev => ({
                ...prev,
                search: localSearch,
                page: 1, // Reset ke halaman 1 saat search berubah
            }));
        }, 400);

        return () => clearTimeout(debounceRef.current);
    }, [localSearch, setParams]);

    // Cari data yang terpilih (Support Object atau ID String)
    const selected = safeData.find((item) => {
        if (typeof value === "object" && value !== null) {
            return String(item.id) === String(value.id);
        }
        return String(item.id) === String(value);
    });

    const handleSelect = (selectedValue) => {
        setOpen(false);
        setLocalSearch(""); // Bersihkan input search
        setParams(prev => ({ ...prev, search: "" })); // Bersihkan param server

        let payload = null;

        if (selectedValue !== "") {
            payload = safeData.find(
                (item) => String(item.id) === String(selectedValue)
            );
        }

        if (onChange) {
            onChange(payload);
        } else {
            setValue?.(payload);
        }
    };

    return (
        <div className="w-full relative" ref={wrapperRef}>
            {label && (
                <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">
                    {label}
                </label>
            )}

            {/* Select Button */}
            <div
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl bg-slate-800/60 border border-slate-600/40 cursor-pointer transition-all duration-200"
                style={{
                    borderColor: open
                        ? "rgba(0,180,216,.5)"
                        : "rgba(71,85,105,.4)",
                    boxShadow: open
                        ? "0 0 0 1px rgba(0,180,216,.2)"
                        : "none",
                }}
            >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                    <FaUser className="text-slate-500 text-xs flex-shrink-0" />

                    <span
                        className={`truncate ${
                            selected
                                ? "text-gray-100"
                                : "text-slate-500"
                        }`}
                    >
                        {selected
                            ? `${selected.internet_number} | ${selected.name}`
                            : placeholder}
                    </span>
                </div>

                <FaChevronDown
                    className={`text-xs text-slate-500 transition-transform duration-200 ${
                        open ? "rotate-180" : ""
                    }`}
                />
            </div>

            {/* Dropdown */}
            {open && (
                <div
                    className="absolute z-[1000] mt-2 w-full rounded-xl overflow-hidden shadow-2xl shadow-black/40"
                    style={{
                        background: "rgba(15,23,42,.95)",
                        border: "1px solid rgba(51,65,85,.5)",
                        backdropFilter: "blur(16px)",
                    }}
                >
                    {/* Search Input */}
                    {searchable && (
                        <div
                            className="p-2.5"
                            style={{
                                borderBottom:
                                    "1px solid rgba(51,65,85,.3)",
                            }}
                        >
                            <div className="relative">
                                <FaMagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs" />

                                <input
                                    autoFocus
                                    type="text"
                                    placeholder="Cari pelanggan..."
                                    value={localSearch}
                                    onChange={(e) =>
                                        setLocalSearch(e.target.value)
                                    }
                                    className="w-full pl-9 pr-9 py-2 text-sm rounded-lg bg-slate-800/80 border border-slate-600/30 text-gray-100 placeholder-slate-500 outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
                                />
                                
                                {/* Icon Loading Server */}
                                {isLoadingData && localSearch && (
                                    <FaSpinner className="absolute right-3 top-1/2 -translate-y-1/2 text-cyan-400 text-xs animate-spin" />
                                )}
                            </div>
                        </div>
                    )}

                    <div className="max-h-64 overflow-y-auto py-1">
                        {/* Option Reset / Semua Pelanggan */}
                        <div
                            onClick={() => handleSelect("")}
                            className={`mx-1 px-3 py-2.5 rounded-lg cursor-pointer transition-all duration-150 flex items-center gap-2 ${
                                !selected
                                    ? "bg-cyan-600/15 text-cyan-400 font-semibold"
                                    : "text-slate-400 hover:bg-slate-700/40 hover:text-gray-100"
                            }`}
                        >
                            <FaUser className="text-xs" />
                            <span>Semua Pelanggan</span>
                        </div>

                        {/* State: Loading Awal / Tidak Ada Data */}
                        {isLoadingData && !localSearch ? (
                            <div className="px-3 py-6 text-center">
                                <FaSpinner className="animate-spin text-slate-500 mx-auto mb-2" />
                                <p className="text-slate-500 text-sm">Memuat data...</p>
                            </div>
                        ) : safeData.length === 0 ? (
                            <div className="px-3 py-6 text-center">
                                <p className="text-slate-500 text-sm italic">
                                    {localSearch 
                                        ? `Tidak ditemukan untuk "${localSearch}"` 
                                        : "Pelanggan tidak ditemukan"}
                                </p>
                            </div>
                        ) : (
                            // Langsung map safeData (karena sudah difilter oleh server)
                            safeData.map((opt) => {
                                const isSelected =
                                    selected?.id === opt.id;

                                return (
                                    <div
                                        key={opt.id}
                                        onClick={() =>
                                            handleSelect(opt.id)
                                        }
                                        className={`mx-1 px-3 py-2.5 rounded-lg cursor-pointer transition-all duration-150 ${
                                            isSelected 
                                                ? "bg-cyan-600/15 text-cyan-400"
                                                : "hover:bg-slate-700/40 text-slate-300"
                                        }`}
                                    >
                                        <div className="flex justify-between items-center gap-3">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <FaUser className="text-xs opacity-60 flex-shrink-0" />

                                                <div className="min-w-0">
                                                    <div className="truncate font-medium">
                                                        {opt.internet_number} |{" "}
                                                        {opt.name}
                                                    </div>

                                                    {opt.profile?.name && (
                                                        <div className="text-[11px] text-slate-500 truncate">
                                                            {opt.profile.name}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {opt.status && (
                                                <span
                                                    className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${
                                                        opt.status ===
                                                        "active"
                                                            ? "bg-green-500/15 text-green-400"
                                                            : "bg-red-500/15 text-red-400"
                                                    }`}
                                                >
                                                    {opt.status}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};